/**
 * This module will configure the application store, producing the
 * reducer composite along with future middleware, store enhancers, etc.
 */
import {
  combineReducers,
  createStore,
  applyMiddleware,
  compose,
  StoreEnhancer,
  AnyAction,
} from "redux";
import { createLogger } from "redux-logger";
import { persistStore, persistReducer } from "redux-persist";
//import localStorage from "redux-persist/lib/storage";
import storageSession from "redux-persist/lib/storage/session";
import thunk, { ThunkDispatch } from "redux-thunk";

/**
 * Custom middleware
 */
import logicMiddleware from "./logic/logicConfig";
import { createSignalRMiddleware } from "./middleware/signalRMiddleware";

/*
 * IMPORTED INTERNAL REDUCERS
 */
import { applicationStateReducer, persistedStateReducer } from "./reducers/applicationStateReducer";
import { controlAppStateReducer } from "./reducers/controlAppStateReducer";
import aframeModeReducer from "./reducers/aframeModeReducer";
import aFrameReviewBoxReducer from "./reducers/aFrameReviewBoxReducer";
import conveyorBoxReviewReducer from "./reducers/conveyorBoxReviewReducer";
import loginInfoReducer from "./reducers/loginInfoReducer";
import layoutHelperReducer from "./reducers/layoutHelperReducer";
import cartReducer from "./reducers/cartReducer";
import missingSkuReducer from "./reducers/missingSkuReducer";
import instructionReducer from "./reducers/instructionStateReducer";
import boxReviewReducer from "./reducers/boxReviewReducer";
import rxModeReducer from "./reducers/rxBoxModeReducer";
import autostoreModeReducer from "./reducers/autostoreModeReducer";
import loadingStateReducer from "./reducers/loadingStateReducer";
import resetLoadingStateTransform from "./transforms/resetLoadingStateTransform";
import responseMessagesReducer from "./reducers/responseMessagesReducer";

// reducers but created by using the newer createSlice method.
import cancelledItemReducer from "./reducers/cancelledItemReducer";
import restockReducer from "./reducers/restockReducer";

import globalStateReducer from "./reducers/globalStateReducer";

/**
 * Type declaration for Redux DevTools (fixes TS error)
 */
declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION__?: (options?: any) => StoreEnhancer;
  }
}

const loadingStatePersistConfig = {
  key: "loadingStateReducer",
  storage: storageSession,
  transforms: [resetLoadingStateTransform],
};

const persistConfig = {
  key: "root",
  storage: storageSession,
  blacklist: [
    "missingSkuReducer",
    "applicationStateReducer",
    "aframeModeReducer",
    "rxModeReducer",
    "loadingStateReducer",
  ],
};

const applicationStatePersistConfig = {
  key: "applicationStateReducer",
  storage: storageSession,
  blacklist: ["loading", "shouldRedirect"],
};
const controlAppStatePersistConfig = {
  key: "controlAppStateReducer",
  storage: storageSession,
};

const aFrameModeStatePersistConfig = {
  key: "aframeModeReducer",
  storage: storageSession,
  blacklist: ["scanDisabled"],
};

const boxReviewModeStatePersistConfig = {
  key: "boxReviewReducer",
  storage: storageSession,
  blacklist: ["scanDisabled"],
};

const rxModeStatePersistConfig = {
  key: "rxModeReducer",
  storage: storageSession,
  blacklist: ["scanDisabled", "signalRConnectionStatus"],
};

const autostoreStatePersistConfig = {
  key: "autostoreModeReducer",
  storage: storageSession,
  blacklist: ["scanDisabled"],
};

//Pass new reducers here.
export const reducerComposite = combineReducers({
  responseMessages: responseMessagesReducer,
  autostoreModeReducer: persistReducer(autostoreStatePersistConfig, autostoreModeReducer),
  aframeModeReducer: persistReducer(aFrameModeStatePersistConfig, aframeModeReducer),
  applicationStateReducer: persistReducer(applicationStatePersistConfig, applicationStateReducer),
  controlAppStateReducer: persistReducer(controlAppStatePersistConfig, controlAppStateReducer),
  rxModeReducer: persistReducer(rxModeStatePersistConfig, rxModeReducer),
  persistedStateReducer,
  aFrameReviewBoxReducer: aFrameReviewBoxReducer,
  conveyorBoxReviewReducer: conveyorBoxReviewReducer, //persistReducer(aFrameModeStatePersistConfig, conveyorBoxReviewReducer), // new reducer for Varberg
  loadingStateReducer: loadingStateReducer, // new reducer for Varberg
  cancelledItemReducer: cancelledItemReducer, // new reducer for Varberg
  restockReducer: restockReducer, // new reducer for Varberg
  globalStateReducer: globalStateReducer, // new reducer for Varberg
  loginInfoReducer,
  layoutHelperReducer,
  cartReducer,
  missingSkuReducer,
  instructionReducer,
  boxReviewReducer: persistReducer(boxReviewModeStatePersistConfig, boxReviewReducer),
});

const persistedReducer = persistReducer(persistConfig, reducerComposite);

const logger = createLogger({
  predicate: (getState, action) =>
    typeof (action as any).type === "string" &&
    !(action as any).type.includes("SET_DISPATCH_TIMESTAMP"),
});

const middlewares: any[] = [logicMiddleware];
if (process.env.NODE_ENV === "development") {
  middlewares.push(logger);
}

const rxAutoSignalRUrl = process.env.REACT_APP_SIGNALR_RX_URL?.trim();

if (rxAutoSignalRUrl) {
  middlewares.push(createSignalRMiddleware(rxAutoSignalRUrl));
}
middlewares.push(thunk as any);

const devtoolsEnhancer = window.__REDUX_DEVTOOLS_EXTENSION__
  ? window.__REDUX_DEVTOOLS_EXTENSION__({
      actionsBlacklist: ["SET_DISPATCH_TIMESTAMP"],
    })
  : compose();

const store = createStore(
  persistedReducer,
  {},
  compose(applyMiddleware(...middlewares), devtoolsEnhancer)
);

export type RootState = ReturnType<typeof reducerComposite>;
export type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

const persistor = persistStore(store);
export { store, persistor };
